<template>
  <div class="layout-root" :class="{ 'is-mobile': isMobile, 'drawer-open': drawerOpen }">
    <div v-if="isMobile && drawerOpen" class="layout-backdrop" @click="closeDrawer"></div>

    <aside v-if="isMobile" class="layout-drawer" :class="{ open: drawerOpen }">
      <div class="layout-drawer__head">
        <img :src="logoUrl" alt="GLT" class="layout-drawer__logo" />
        <span>{{ $t('app.name') }}</span>
      </div>
      <nav class="layout-drawer__nav">
        <section v-for="group in groupedMenu" :key="group.key" class="drawer-section">
          <div class="drawer-section__label">{{ $t('navGroups.' + group.key) }}</div>
          <template v-for="item in group.items">
            <div v-if="item.children && item.children.length" :key="'g-' + item.id">
              <button
                type="button"
                class="drawer-item drawer-item--parent"
                :class="{ active: isGroupActive(item) }"
                @click="toggleGroup(item.id)"
              >
                <i :class="item.icon"></i>
                <span>{{ item.label }}</span>
                <i
                  class="drawer-caret"
                  :class="expandedGroups.indexOf(item.id) >= 0 ? 'el-icon-arrow-up' : 'el-icon-arrow-down'"
                ></i>
              </button>
              <div v-show="expandedGroups.indexOf(item.id) >= 0" class="drawer-children">
                <router-link
                  v-for="child in item.children"
                  :key="child.id"
                  :to="child.routePath"
                  class="drawer-item drawer-item--child"
                  :class="{ active: isRouteActive(child.routePath) }"
                  @click.native="closeDrawer"
                >
                  <span>{{ child.label }}</span>
                </router-link>
              </div>
            </div>
            <router-link
              v-else
              :key="'l-' + item.id"
              :to="item.routePath"
              class="drawer-item"
              :class="{ active: isRouteActive(item.routePath) }"
              @click.native="closeDrawer"
            >
              <i :class="item.icon"></i>
              <span>{{ item.label }}</span>
            </router-link>
          </template>
        </section>
      </nav>
    </aside>

    <div class="layout-body">
      <header class="layout-header">
        <div class="layout-header__brand-row">
          <button
            v-if="isMobile"
            type="button"
            class="layout-header__menu"
            aria-label="Menu"
            @click="drawerOpen = !drawerOpen"
          >
            <i class="el-icon-s-unfold"></i>
          </button>
          <div class="layout-header__brand" @click="$router.push('/home')">
            <img :src="logoUrl" alt="GLT" class="layout-header__logo" />
            <span v-if="!isMobile" class="layout-header__name">{{ $t('app.name') }}</span>
          </div>
        </div>

        <glt-top-nav
          v-if="!isMobile"
          :groups="groupedMenu"
          :badge-value="badgeValue"
          :is-route-active="isRouteActive"
        />

        <div class="layout-header__actions">
          <router-link
            v-if="!isMobile && taskCount"
            to="/home"
            class="layout-chip"
            :title="$t('dashboardPage.tasksTitle')"
          >
            <i class="el-icon-s-order"></i>
            <span>{{ taskCount }}</span>
          </router-link>
          <router-link
            v-if="!isMobile && visitCount"
            to="/schedule/calendar"
            class="layout-chip"
            :title="$t('schedulePage.upcoming')"
          >
            <i class="el-icon-date"></i>
            <span>{{ visitCount }}</span>
          </router-link>
          <el-dropdown trigger="click" @command="handleCommand">
            <button type="button" class="layout-header__user">
              <span class="layout-header__avatar">{{ displayNameInitial }}</span>
              <span v-if="!isMobile" class="layout-header__username">{{ displayName }}</span>
              <i class="el-icon-arrow-down"></i>
            </button>
            <el-dropdown-menu slot="dropdown" class="layout-user-menu">
              <el-dropdown-item disabled class="dropdown-meta">
                <div>{{ displayName }}</div>
                <div v-if="username">{{ username }}</div>
              </el-dropdown-item>
              <el-dropdown-item
                v-for="option in $languageOptions"
                :key="'loc-' + option.value"
                :class="{ 'is-checked': $locale === option.value }"
                :command="'locale:' + option.value"
              >
                {{ option.label }}
              </el-dropdown-item>
              <el-dropdown-item divided :class="{ 'is-checked': themeMode === 'light' }" command="theme:light">
                {{ $t('common.lightMode') }}
              </el-dropdown-item>
              <el-dropdown-item :class="{ 'is-checked': themeMode === 'dark' }" command="theme:dark">
                {{ $t('common.darkMode') }}
              </el-dropdown-item>
              <el-dropdown-item icon="el-icon-switch-button" divided command="logout">
                {{ $t('common.logout') }}
              </el-dropdown-item>
            </el-dropdown-menu>
          </el-dropdown>
        </div>
      </header>

      <main class="layout-main" :class="{ 'layout-main--compact': compactWorkspaceHeader }">
        <router-view />
      </main>
    </div>
  </div>
</template>

<script>
import store from '@/store'
import logo from '@/assets/images/logo.png'
import GltTopNav from '@/components/GltTopNav.vue'
import { NAV_GROUP_ORDER, normalizeMenuGroup, normalizeRoute, routeIcon, routeTitleKey } from '@/config/navigation'

const ROUTE_REDIRECT_MAP = {
  '/dashboard': '/home',
  '/leads': '/lead/list',
  '/customers': '/customer/list',
  '/opportunities': '/lead/list',
  '/quotes': '/quote/list',
  '/contracts': '/contract/list',
  '/projects': '/project/list',
  '/subcontractors': '/subcontractor/list',
  '/reports': '/report/dashboard',
  '/invoices': '/finance/overview',
  '/system': '/system/overview'
}

const PAGE_SUBTITLE_PREFIXES = [
  { prefix: '/system/contract-templates/new', key: 'workspaceHints.contractTemplateForm' },
  { prefix: '/system/contract-templates/edit', key: 'workspaceHints.contractTemplateForm' },
  { prefix: '/system/contract-templates', key: 'pipelineList.contractTemplates' },
  { prefix: '/system/users', key: 'settingsPageExtra.usersSubtitle' },
  { prefix: '/system/roles', key: 'settingsPageExtra.rolesSubtitle' },
  { prefix: '/system/menus', key: 'settingsPageExtra.menusSubtitle' },
  { prefix: '/system/mail-settings', key: 'emailConfigPage.subtitle' },
  { prefix: '/system/overview', key: 'settingsPage.subtitle' },
  { prefix: '/lead/list', key: 'pipelineList.leads' },
  { prefix: '/lead/new', key: 'workspaceHints.leadNew' },
  { prefix: '/lead/detail', key: 'workspaceHints.leadDetail' },
  { prefix: '/customer/list', key: 'pipelineList.customers' },
  { prefix: '/customer/new', key: 'workspaceHints.customerNew' },
  { prefix: '/customer/edit', key: 'workspaceHints.customerEdit' },
  { prefix: '/quote/list', key: 'pipelineList.quotes' },
  { prefix: '/quote/new', key: 'workspaceHints.quoteNew' },
  { prefix: '/quote/edit', key: 'workspaceHints.quoteEdit' },
  { prefix: '/contract/list', key: 'pipelineList.contracts' },
  { prefix: '/contract/from-quote', key: 'workspaceHints.contractFromQuote' },
  { prefix: '/contract/detail', key: 'workspaceHints.contractDetail' },
  { prefix: '/change-order/list', key: 'pipelineList.changeOrders' },
  { prefix: '/project/list', key: 'pipelineList.projects' },
  { prefix: '/project/detail', key: 'workspaceHints.projectDetail' },
  { prefix: '/subcontractor/list', key: 'pipelineList.subcontractors' },
  { prefix: '/subcontractor/new', key: 'workspaceHints.subcontractorNew' },
  { prefix: '/subcontractor/edit', key: 'workspaceHints.subcontractorEdit' },
  { prefix: '/finance/lead-cost', key: 'workspaceHints.leadCostMonth' },
  { prefix: '/finance/overview', key: 'workspaceHints.financeOverview' },
  { prefix: '/sales-amount/list', key: 'workspaceHints.salesAmount' },
  { prefix: '/report/dashboard', key: 'reportPage.subtitle' },
  { prefix: '/schedule/calendar', key: 'schedulePage.subtitle' },
  { prefix: '/home', key: 'dashboardPage.subtitle' },
  { prefix: '/reports', key: 'reportPage.subtitle' },
  { prefix: '/report', key: 'reportPage.subtitle' },
  { prefix: '/schedule', key: 'schedulePage.subtitle' },
  { prefix: '/finance', key: 'workspaceHints.financeOverview' },
  { prefix: '/sales-amount', key: 'workspaceHints.salesAmount' },
  { prefix: '/project', key: 'projectPage.subtitle' },
  { prefix: '/system', key: 'settingsPage.subtitle' }
]

const PAGE_SUBTITLE_PREFIXES_SORTED = [...PAGE_SUBTITLE_PREFIXES].sort(
  (a, b) => b.prefix.length - a.prefix.length
)

const MOBILE_BREAKPOINT = 992

function deepClone(nodes) {
  return (nodes || []).map(item => ({
    ...item,
    children: deepClone(item.children || [])
  }))
}

export default {
  name: 'Layout',
  components: { GltTopNav },
  data() {
    return {
      expandedGroups: [],
      logoUrl: logo,
      isMobile: false,
      drawerOpen: false
    }
  },
  computed: {
    baseMenuTree() {
      const tree = deepClone(store.state.menuTree || [])
      const routePaths = []
      const walk = nodes => {
        nodes.forEach(node => {
          routePaths.push(node.routePath)
          walk(node.children || [])
        })
      }
      walk(tree)
      if (!routePaths.some(p => String(p || '').includes('/dashboard'))) {
        tree.unshift({
          id: 'menu-dashboard',
          menuName: 'Dashboard',
          routePath: '/dashboard',
          children: []
        })
      }
      if (!routePaths.some(p => String(p || '').includes('/schedule'))) {
        tree.push({
          id: 'menu-schedule',
          menuName: 'Schedule',
          routePath: '/schedule/calendar',
          children: []
        })
      }
      return tree
    },
    groupedMenu() {
      const buckets = NAV_GROUP_ORDER.reduce((acc, key) => {
        acc[key] = []
        return acc
      }, {})

      this.baseMenuTree.forEach(item => {
        const normalized = this.decorateMenuItem(item)
        buckets[normalized.group] = buckets[normalized.group] || []
        buckets[normalized.group].push(normalized)
      })

      return NAV_GROUP_ORDER
        .map(key => ({ key, items: buckets[key] || [] }))
        .filter(group => group.items.length)
    },
    username() {
      const u = store.state.userInfo
      return (u && u.username) || ''
    },
    displayName() {
      const u = store.state.userInfo
      if (!u) return 'User'
      return (u.realName && u.realName.trim()) || u.username || 'User'
    },
    displayNameInitial() {
      return (this.displayName || '?').charAt(0).toUpperCase()
    },
    roleLabel() {
      const roles = (store.state.userInfo && store.state.userInfo.roleCodes) || []
      return roles.length ? roles.join(' · ') : ''
    },
    pageTitle() {
      const matched = this.$route.matched
      const leaf = matched.length ? matched[matched.length - 1] : null
      const meta = leaf && leaf.meta
      if (meta && meta.titleKey) return this.$t(meta.titleKey)
      const titleKey = routeTitleKey(this.$route.path)
      if (titleKey) return this.$t(titleKey)
      if (meta && meta.title) return meta.title
      return this.$t('app.name')
    },
    pageSubtitle() {
      const path = this.$route.path
      const matched = this.$route.matched
      const leaf = matched.length ? matched[matched.length - 1] : null
      const meta = leaf && leaf.meta
      if (meta && meta.subtitleKey) return this.$t(meta.subtitleKey)
      const hit = PAGE_SUBTITLE_PREFIXES_SORTED.find(item => path.startsWith(item.prefix))
      if (hit) return this.$t(hit.key)
      return this.$t('app.tagline')
    },
    taskCount() {
      const tasks = (store.state.workbench && store.state.workbench.tasks) || []
      return tasks.length
    },
    visitCount() {
      const visits = (store.state.workbench && store.state.workbench.upcomingVisits) || []
      return visits.length
    },
    compactWorkspaceHeader() {
      return this.$route.matched.some(r => r.meta && r.meta.compactWorkspaceHeader)
    },
    themeMode() {
      return this.$store.state.themeMode
    }
  },
  watch: {
    $route() {
      this.autoExpandGroup(this.$route.path)
      this.closeDrawer()
    }
  },
  created() {
    this.autoExpandGroup(this.$route.path)
    this.updateViewport()
    window.addEventListener('resize', this.updateViewport)
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.updateViewport)
  },
  methods: {
    updateViewport() {
      this.isMobile = window.innerWidth < MOBILE_BREAKPOINT
      if (!this.isMobile) this.drawerOpen = false
    },
    closeDrawer() {
      this.drawerOpen = false
    },
    decorateMenuItem(item) {
      const routePath = ROUTE_REDIRECT_MAP[item.routePath] || item.routePath
      const children = (item.children || []).map(child => this.decorateMenuItem(child))
      const titleKey = item.i18nKey || routeTitleKey(routePath)
      return {
        ...item,
        routePath,
        icon: item.iconKey || routeIcon(routePath, 'el-icon-menu'),
        label: titleKey ? this.$t(titleKey) : item.menuName,
        group: normalizeMenuGroup(item.menuGroup, routePath),
        children
      }
    },
    badgeValue(item) {
      const badges = (store.state.workbench && store.state.workbench.badges) || {}
      const route = normalizeRoute(item.routePath)
      if (route.startsWith('/quote') || route.startsWith('/quotes')) return badges.pendingQuotes || 0
      if (route.startsWith('/contract') || route.startsWith('/contracts')) return badges.awaitingContracts || 0
      if (route.startsWith('/schedule')) return badges.upcomingVisits || 0
      if (route.startsWith('/finance') || route.startsWith('/invoices')) return badges.pendingInvoices || 0
      return 0
    },
    isRouteActive(routePath) {
      return normalizeRoute(this.$route.path) === normalizeRoute(routePath)
        || normalizeRoute(this.$route.path).startsWith(normalizeRoute(routePath) + '/')
    },
    isGroupActive(item) {
      return item.children && item.children.some(child => this.isRouteActive(child.routePath))
    },
    toggleGroup(id) {
      const idx = this.expandedGroups.indexOf(id)
      if (idx >= 0) this.expandedGroups.splice(idx, 1)
      else this.expandedGroups.push(id)
    },
    autoExpandGroup(path) {
      this.groupedMenu.forEach(group => {
        group.items.forEach(item => {
          if (
            item.children &&
            item.children.length &&
            item.children.some(child => this.isRouteActive(child.routePath) || String(path).startsWith(child.routePath))
          ) {
            if (this.expandedGroups.indexOf(item.id) < 0) this.expandedGroups.push(item.id)
          }
        })
      })
    },
    handleLocaleChange(locale) {
      this.$store.dispatch('switchLocale', locale)
    },
    handleThemeChange(mode) {
      this.$store.dispatch('switchThemeMode', mode)
    },
    handleCommand(cmd) {
      if (String(cmd).startsWith('locale:')) {
        this.handleLocaleChange(cmd.slice(7))
        return
      }
      if (cmd === 'theme:light' || cmd === 'theme:dark') {
        this.handleThemeChange(cmd.slice(6))
        return
      }
      if (cmd === 'logout') {
        this.$confirm(this.$t('common.logoutConfirm'), this.$t('common.logout'), {
          confirmButtonText: this.$t('common.logoutAction'),
          cancelButtonText: this.$t('common.cancel'),
          type: 'warning'
        })
          .then(() => {
            this.$store.dispatch('logout')
            this.$router.replace('/login')
          })
          .catch(() => {})
      }
    }
  }
}
</script>

<style scoped>
.layout-root {
  display: flex;
  height: 100vh;
  overflow: hidden;
  background: var(--color-bg);
}

.layout-body {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.layout-header {
  display: flex;
  align-items: center;
  gap: 12px;
  min-height: 52px;
  padding: 0 16px 0 20px;
  background: var(--layout-header-bg);
  border-bottom: 1px solid var(--color-border-soft);
  flex-shrink: 0;
  z-index: 10;
}

.layout-header__brand-row {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
}

.layout-header__menu {
  width: 34px;
  height: 34px;
  border: 0;
  border-radius: 8px;
  background: transparent;
  color: var(--color-text-sub);
  cursor: pointer;
  font-size: 18px;
}

.layout-header__menu:hover {
  background: var(--layout-hover-bg);
  color: var(--color-brand);
}

.layout-header__brand {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
}

.layout-header__logo {
  width: 28px;
  height: 28px;
  border-radius: 6px;
  object-fit: cover;
}

.layout-header__name {
  font-size: 14px;
  font-weight: 500;
  color: var(--color-text);
  letter-spacing: -0.01em;
}

.layout-header__actions {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
  margin-left: auto;
}

.layout-chip {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  height: 28px;
  padding: 0 10px;
  border-radius: 14px;
  background: var(--color-surface-soft);
  color: var(--color-text-sub);
  text-decoration: none;
  font-size: 12px;
  transition: background 0.15s ease, color 0.15s ease;
}

.layout-chip:hover {
  background: var(--color-brand-soft);
  color: var(--color-brand);
}

.layout-chip i {
  font-size: 13px;
}

.layout-header__user {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 8px 4px 4px;
  border: 0;
  border-radius: 20px;
  background: transparent;
  cursor: pointer;
  font-family: inherit;
}

.layout-header__user:hover {
  background: var(--layout-hover-bg);
}

.layout-header__avatar {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: var(--color-brand-soft);
  color: var(--color-brand);
  font-size: 12px;
  font-weight: 500;
}

.layout-header__username {
  max-width: 120px;
  font-size: 13px;
  color: var(--color-text);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.layout-header__user i {
  font-size: 12px;
  color: var(--color-text-muted);
}

.layout-main {
  flex: 1;
  overflow: auto;
  min-height: 0;
  padding: 16px 0 24px;
}

.layout-main--compact {
  padding-top: 12px;
}

.layout-backdrop {
  position: fixed;
  inset: 0;
  z-index: 998;
  background: rgba(15, 23, 42, 0.3);
}

.layout-drawer {
  position: fixed;
  top: 0;
  left: 0;
  z-index: 999;
  width: 260px;
  height: 100vh;
  display: flex;
  flex-direction: column;
  background: var(--layout-sidebar-bg);
  border-right: 1px solid var(--layout-sidebar-border);
  transform: translateX(-100%);
  transition: transform 0.25s ease;
}

.layout-drawer.open {
  transform: translateX(0);
}

.layout-drawer__head {
  display: flex;
  align-items: center;
  gap: 10px;
  height: 52px;
  padding: 0 16px;
  border-bottom: 1px solid var(--layout-sidebar-border);
  font-size: 14px;
  font-weight: 500;
  color: var(--color-text);
}

.layout-drawer__logo {
  width: 28px;
  height: 28px;
  border-radius: 6px;
  object-fit: cover;
}

.layout-drawer__nav {
  flex: 1;
  overflow-y: auto;
  padding: 12px 10px 16px;
}

.drawer-section + .drawer-section {
  margin-top: 14px;
}

.drawer-section__label {
  padding: 0 10px 6px;
  font-size: 11px;
  font-weight: 500;
  color: var(--color-text-muted);
}

.drawer-item {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  min-height: 36px;
  padding: 0 10px;
  margin-bottom: 2px;
  border: 0;
  border-radius: 6px;
  background: transparent;
  color: var(--color-text-sub);
  text-decoration: none;
  font-size: 13.5px;
  font-family: inherit;
  text-align: left;
  cursor: pointer;
}

.drawer-item i:first-child {
  width: 16px;
  text-align: center;
  font-size: 14px;
}

.drawer-item span {
  flex: 1;
}

.drawer-item:hover {
  background: var(--layout-hover-bg);
  color: var(--color-text);
}

.drawer-item.active {
  background: var(--color-brand-soft);
  color: var(--color-brand);
  font-weight: 500;
}

.drawer-item--parent {
  font-family: inherit;
}

.drawer-children {
  padding: 0 0 4px 12px;
}

.drawer-item--child {
  min-height: 32px;
  font-size: 13px;
  padding-left: 26px;
}

.drawer-caret {
  font-size: 11px;
  color: var(--color-text-muted);
}

@media (max-width: 991px) {
  .layout-header {
    padding: 0 14px;
  }

  .layout-main {
    padding: 12px 0 20px;
  }
}
</style>

<style>
.dropdown-meta {
  line-height: 1.6 !important;
  font-size: 12px !important;
  color: #909399 !important;
  cursor: default !important;
}

.layout-user-menu .el-dropdown-menu__item.is-checked {
  color: var(--color-brand) !important;
  font-weight: 500;
}
</style>
